import { Client } from "@stomp/stompjs";
import { captureException } from "@sentry/vue";
import { useAppStore } from "~stores/App.ts";
import { useUserStore } from "~stores/User.ts";
import { refreshGridsOfResource, updateGridRows } from "~utils/Grids/RefreshGrid.ts";
import { setActiveLanguage } from "@q-food/q-food";
import AppNotificationToastBody from "~components/AppNotificationToastBody.vue";
import {
	Channel,
	gridRowUpdates,
	userNotifications,
	userOpenGrid,
	userRefreshApp,
	userRefreshCompanyActiveLanguage,
	userRefreshGrid,
	userRefreshUserActiveLanguage,
} from "~utils/SocketChannels.ts";

let socketUrl;
if (import.meta.env.VITE_QFOOD_WEBSOCKET_HOST == undefined || import.meta.env.VITE_QFOOD_WEBSOCKET_HOST == "") {
	socketUrl = `wss://${window.location.host}`;
} else {
	socketUrl = import.meta.env.VITE_QFOOD_WEBSOCKET_HOST;
}

const socketClient = new Client({
	brokerURL: `${socketUrl}/ws`,
	heartbeatOutgoing: 10_000,
	connectHeaders: {
		"x-auth-token": localStorage.getItem("qfood-session-token")!,
	},
});

const onConnectListeners: ((client: Client) => void)[] = [];

export function onConnect(func: (client: Client) => void) {
	if (socketClient.active && socketClient.connected) {
		func(socketClient);
	}

	onConnectListeners.push(func);
}

function subscribeClient<T>(channel: Channel<T>, handler: (payload: T) => void) {
	socketClient.subscribe(channel.destination, (msg) => handler(channel.decode(msg.body)));
}

socketClient.onConnect = (frame) => {
	const { toast } = useToast();

	onConnectListeners.forEach((listener) => listener(socketClient));

	subscribeClient(userNotifications, async (notification) => {
		const type = notification.type.toLowerCase();

		if (!(type in toast)) {
			return;
		}

		await new Promise((r) => setTimeout(r, 300));

		const userStore = useUserStore();
		await userStore.refreshNotifications();

		window.dispatchEvent(new CustomEvent("food:notification", { detail: notification }));

		const details: string[] = notification.details ?? [];

		// A notification that carries details is deliberately not marked as read when its toast is
		// dismissed (see onDismiss below), because the details still have to be read. Render the body as a
		// component in that case, so the details can be opened with an eye right here instead of only in
		// the notification dropdown. Only a non-string description is rendered as a component by
		// vue-sonner (its `action.label` is always coerced to text), and its props go through
		// `componentProps`.
		toast[type](notification.title, {
			...(details.length > 0
				? {
						description: AppNotificationToastBody,
						componentProps: { message: notification.message, details },
						// Long enough to actually open and read the details before it disappears.
						duration: 15000,
					}
				: { description: notification.message }),
			onDismiss: async () => {
				if (details.length === 0 && notification.priority === false) {
					await userStore.readNotification(notification);
				}
			},
		});
	});

	subscribeClient(userRefreshGrid, (resources) => {
		resources.forEach((resource) => refreshGridsOfResource(resource));
	});

	// Patch just the changed rows in place instead of refetching the whole grid. A client ignores any row
	// it doesn't have loaded and can only refetch through its own ACL-scoped endpoint, so a single global
	// topic is safe.
	subscribeClient(gridRowUpdates, ({ resource, rowIds }) => {
		updateGridRows(resource, rowIds);
	});

	// Assistant "open a grid" signal: { resource, criteria }. Handled in App.vue (needs router/setup context).
	subscribeClient(userOpenGrid, ({ resource, criteria }) => {
		window.dispatchEvent(new CustomEvent("food:grid:open", { detail: { resource, criteria } }));
	});

	subscribeClient(userRefreshApp, () => {
		useAppStore().refreshApp();
	});

	subscribeClient(userRefreshUserActiveLanguage, async (languageCode) => {
		await setActiveLanguage(languageCode);
		useAppStore().refreshApp();
	});

	subscribeClient(userRefreshCompanyActiveLanguage, async (body) => {
		const [languageCode, companyId] = body.split(" ");
		const activeCompany = useUserStore().activeCompany;
		const activeLanguageCode = useUserStore().activeLanguageCode;
		if (activeCompany.id == companyId && activeLanguageCode != languageCode.toLowerCase()) {
			await setActiveLanguage(languageCode);
		}
		useAppStore().refreshApp();
	});
};

socketClient.onWebSocketClose = (event) => {
	if (event?.code === 1009) {
		console.log("STOMP socket closed (1009): frame too big for broker buffer", event.reason);
		captureException(new Error(`STOMP socket closed (1009): ${event.reason || "message too big"}`));
	}
};

export { socketClient };
